import { useEffect, useState } from 'react';
import { ToolProps } from '.';
import {
  MarkerPin01,
  User01,
  Calendar,
  Clock,
  Check,
  ArrowNarrowUpRight,
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  Edit01,
  CheckCircleBroken,
} from '@insight-health-ai/roseate/icons';
import classNames from 'classnames';
import { Button } from '@insight-health-ai/roseate/components';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(customParseFormat);

import {
  EhrAppointmentSlot,
  EhrDepartment,
  EhrProvider,
  getEhrAppointmentSlotsApi,
  getEhrDepartmentsApi,
  getEhrProvidersApi,
  createEhrAppointmentApi,
} from 'src/api/appointment';
import { ErrorScreen, ScreenLoader } from 'src/components';

type AppointmentSchedulerProps = ToolProps<{
  appointment_date: string;
  appointment_time: string;
  appointment_type: string;
  location: string;
}>;

type AppointmentSchedulerPayload = {
  location?: EhrDepartment;
  provider?: EhrProvider;
  appointmentSlot?: EhrAppointmentSlot;
};

export const SlotOptions = ({
  startDate,
  endDate,
  selectedData,
  onSelect,
}: {
  startDate: dayjs.Dayjs;
  endDate: dayjs.Dayjs;
  selectedData: AppointmentSchedulerPayload;
  onSelect: (value: EhrAppointmentSlot) => void;
}) => {
  const [loading, setLoading] = useState(false);
  const [slots, setSlots] = useState<EhrAppointmentSlot[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    getEhrAppointmentSlotsApi({
      department_id: selectedData?.location?.department_id,
      provider_id: selectedData.provider?.id,
      start_date: startDate.format('YYYY-MM-DD'),
      end_date: endDate.format('YYYY-MM-DD'),
    })
      .then((response) => {
        setSlots(response.data);
      })
      .catch((error) => {
        setError(error.message);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [
    selectedData.location?.department_id,
    selectedData.provider?.id,
    startDate,
    endDate,
  ]);

  if (loading) {
    return (
      <div className="h-[320px]">
        <ScreenLoader />
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-[320px]">
        <ErrorScreen
          title="Error loading slots"
          description="Please try again later"
        />
      </div>
    );
  }

  // Filter slots for the selected date range
  const filteredSlots = slots
    .filter((slot) => {
      const slotDate = dayjs(slot.datetime, 'MM/DD/YYYY HH:mm');

      // Use startDate as filter if current date is after startDate, otherwise show all up to endDate
      // Use endOf('day') to include all slots on the last day of the range
      const now = dayjs();
      return (
        slotDate.isAfter(startDate.startOf('day')) &&
        slotDate.isAfter(now) &&
        slotDate.isBefore(endDate.endOf('day'))
      );
    })
    .sort((a, b) => {
      const aTime = dayjs(a.datetime, 'MM/DD/YYYY HH:mm');
      const bTime = dayjs(b.datetime, 'MM/DD/YYYY HH:mm');
      return aTime.valueOf() - bTime.valueOf();
    });

  if (filteredSlots.length === 0) {
    return (
      <div className="flex items-center justify-center h-32 text-gray-500">
        No available slots for the selected date
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {filteredSlots.map((slot) => {
        const slotTime = dayjs(slot.datetime, 'MM/DD/YYYY HH:mm');
        const isSelected =
          selectedData.appointmentSlot?.appointment_id === slot.appointment_id;

        return (
          <div
            key={slot.appointment_id}
            className={classNames(
              'flex items-center gap-3 p-4 border rounded-lg transition-colors cursor-pointer',
              {
                'border-primary bg-green-50': isSelected,
                'border-gray-200 bg-white hover:border-primary': !isSelected,
              }
            )}
            onClick={() => onSelect(slot)}
          >
            <div className="text-gray-500 text-lg">
              <Clock />
            </div>
            <div className="flex flex-1 flex-col items-start gap-1">
              <div className="font-medium text-lg">
                {slotTime.format('ddd, MMM D · h:mm A')}
              </div>
              <div className="text-sm text-gray-500">
                {slot.duration} min · {slot.appointment_type}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export const DepartmentOptions = ({
  selectedData,
  onSelect,
}: {
  selectedData: AppointmentSchedulerPayload;
  onSelect: (value: EhrDepartment) => void;
}) => {
  const [loading, setLoading] = useState(false);
  const [departments, setDepartments] = useState<EhrDepartment[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    getEhrDepartmentsApi()
      .then((response) => {
        setDepartments(response.data);
      })
      .catch((error) => {
        setError(error.message);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const selected = selectedData.location;

  if (loading) {
    return (
      <div className="h-[320px]">
        <ScreenLoader />
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-[320px]">
        <ErrorScreen
          title="Error loading departments"
          description="Please try again later"
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {departments.map((option) => (
        <div
          key={option.department_id}
          className={classNames(
            'flex items-start gap-3 p-4 border border-gray-200 hover:border-primary active:bg-green-50 rounded-lg bg-white cursor-pointer',
            {
              'border-primary bg-green-50':
                option.department_id === selected?.department_id,
            }
          )}
          onClick={() => {
            onSelect(option);
          }}
        >
          <div className="text-green-500 text-lg mt-1">
            <MarkerPin01 />
          </div>
          <div className="flex-1">
            <div className="font-semibold text-gray-800 text-base mb-1">
              {option.name}
            </div>
            <div className="text-gray-500 text-sm mb-3">
              {[option.address, option.address2, option.city, option.state]
                .filter(Boolean)
                .join(', ')}
            </div>
            <Button
              variant="outlined"
              icon={<ArrowNarrowUpRight />}
              onClick={(e) => {
                e.stopPropagation();
                window.open(
                  `https://www.google.com/maps/dir/?api=1&destination=${option.latitude},${option.longitude}`,
                  '_blank'
                );
              }}
            >
              View Directions
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
};

export const ProviderOptions = ({
  selectedData,
  onSelect,
}: {
  selectedData: AppointmentSchedulerPayload;
  onSelect: (value: EhrProvider) => void;
}) => {
  const [loading, setLoading] = useState(false);
  const [providers, setProviders] = useState<EhrProvider[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    getEhrProvidersApi()
      .then((response) => {
        setProviders(
          response.data
            .filter((provider) => provider.name)
            .sort((a, b) => a.name.localeCompare(b.name))
        );
      })
      .catch((error) => {
        setError(error.message);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const selected = selectedData.provider;

  if (loading) {
    return (
      <div className="h-[320px]">
        <ScreenLoader />
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-[320px]">
        <ErrorScreen
          title="Error loading providers"
          description="Please try again later"
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {providers.map((option) => (
        <div
          key={option.id}
          className={classNames(
            'flex items-start gap-3 p-4 border border-gray-200 hover:border-primary active:bg-green-50 rounded-lg bg-white cursor-pointer',
            {
              'border-primary bg-green-50': option.id === selected?.id,
            }
          )}
          onClick={() => {
            onSelect(option);
          }}
        >
          <div className="text-green-500 text-lg mt-1">
            <User01 />
          </div>
          <div className="flex-1">
            <div className="font-semibold text-gray-800 text-base mb-1">
              {option.name}
            </div>
            <div className="text-gray-500 text-sm">{option.specialization}</div>
          </div>
        </div>
      ))}
    </div>
  );
};

export const AppointmentScheduler = ({
  // name,
  args,
  // id,
  response,
  onComplete,
}: AppointmentSchedulerProps) => {
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [formData, setFormData] = useState<AppointmentSchedulerPayload>({});
  const [currentDateRange, setCurrentDateRange] = useState<{
    start: dayjs.Dayjs;
    end: dayjs.Dayjs;
  }>({
    start: dayjs().startOf('week').add(1, 'day'),
    end: dayjs().startOf('week').add(7, 'day'),
  });

  const updateFormData = <T extends keyof typeof formData>(
    key: T,
    value: (typeof formData)[T]
  ) => {
    setFormData((prev: any) => ({ ...prev, [key]: value }));
  };

  if (response) {
    const parsed: AppointmentSchedulerPayload =
      response.content_type === 'serialized_json'
        ? (() => {
            try {
              return JSON.parse(response.content as string) as any;
            } catch {
              return null;
            }
          })()
        : null;

    const provider = parsed?.provider ?? ({ name: 'Unknown' } as EhrProvider);

    const location = parsed?.location;

    const dateTimeLabel = (() => {
      const datetime = parsed?.appointmentSlot?.datetime ?? undefined;
      if (datetime) {
        const d = dayjs(datetime, 'MM/DD/YYYY HH:mm');
        if (d.isValid()) return d.format('ddd, MMM D · h:mm A');
      }

      return 'Unknown';
    })();

    return (
      <div className="p-4 shadow rounded-lg bg-white">
        <div className="flex flex-col gap-4">
          <div className="text-primary text-5xl">
            <CheckCircleBroken />
          </div>

          <div className="font-semibold text-xl">Appt scheduled</div>
          <div className="flex flex-col gap-2">
            {provider.name && (
              <div className="flex items-start gap-2 text-gray-700">
                <User01 className="text-lg mt-0.5 flex-shrink-0" />
                <div>
                  <div className="font-medium">{provider.name}</div>
                  {provider.specialization && (
                    <div className="text-gray-500">
                      {provider.specialization}
                    </div>
                  )}
                </div>
              </div>
            )}

            {location ? (
              <div className="flex items-start gap-2 text-gray-700">
                <MarkerPin01 className="text-lg mt-0.5 flex-shrink-0" />
                <div>
                  <div className="font-medium">{location.name}</div>
                  <div className="text-gray-500">
                    {[
                      location.address,
                      location.address2,
                      location.city,
                      location.state,
                    ]
                      .filter(Boolean)
                      .join(', ')}
                  </div>
                </div>
              </div>
            ) : null}

            {dateTimeLabel && (
              <div className="flex items-start gap-2 text-gray-700">
                <Calendar className="text-lg mt-0.5 flex-shrink-0" />
                <div className="font-medium">{dateTimeLabel}</div>
              </div>
            )}

            {response.content_type !== 'serialized_json' &&
              response.content && (
                <div className="text-sm text-gray-500">{response.content}</div>
              )}
          </div>
        </div>
      </div>
    );
  }

  const valueTabs = [
    {
      label: 'Location',
      key: 'location' as const,
      title: 'Select Location',
      icon: <MarkerPin01 />,
      renderFinalValue: (value: (typeof formData)['location']) => value?.name,
      content: (
        <DepartmentOptions
          selectedData={formData}
          onSelect={(value) => {
            updateFormData('location', value);
            setCurrentStep(1);
          }}
        />
      ),
    },
    {
      label: 'Provider',
      key: 'provider' as const,
      title: 'Select Provider',
      icon: <User01 />,
      renderFinalValue: (value: (typeof formData)['provider']) => value?.name,
      content: (
        <ProviderOptions
          selectedData={formData}
          onSelect={(value) => {
            updateFormData('provider', value);
            setCurrentStep(2);
          }}
        />
      ),
    },
    {
      label: 'Date & Time',
      key: 'appointmentSlot' as const,
      title: 'Select Appointment Slot',
      icon: <Calendar />,
      renderFinalValue: (value: (typeof formData)['appointmentSlot']) =>
        value?.datetime
          ? dayjs(value.datetime, 'MM/DD/YYYY HH:mm').format(
              'ddd, MMM D · h:mm A'
            )
          : 'Unknown',
      content: (
        <div className="flex flex-col gap-4">
          {/* Navigation Bar */}
          <div className="flex items-center justify-between">
            <Button
              variant="text"
              size="sm"
              appearance="secondary"
              icon={<ChevronLeft />}
              disabled={currentDateRange.start.isSame(
                dayjs().startOf('week').add(1, 'day'),
                'day'
              )}
              onClick={() => {
                const newStartDate = dayjs(currentDateRange.start).subtract(
                  7,
                  'day'
                );
                setCurrentDateRange({
                  start: newStartDate,
                  end: newStartDate.add(6, 'day'),
                });
              }}
            >
              Previous
            </Button>
            <div className="font-medium text-gray-700 text-sm text-center">
              {currentDateRange.start.format('MMM DD')} -{' '}
              {currentDateRange.end.format('MMM DD')}
            </div>
            <Button
              variant="text"
              size="sm"
              appearance="secondary"
              icon={{ icon: <ChevronRight />, position: 'end' }}
              onClick={() => {
                const newStartDate = dayjs(currentDateRange.start).add(
                  7,
                  'day'
                );
                setCurrentDateRange({
                  start: newStartDate,
                  end: newStartDate.add(6, 'day'),
                });
              }}
            >
              Next
            </Button>
          </div>

          <SlotOptions
            startDate={currentDateRange.start}
            endDate={currentDateRange.end}
            selectedData={formData}
            onSelect={(value) => {
              updateFormData('appointmentSlot', value);
              setCurrentStep(3);
            }}
          />
        </div>
      ),
    },
  ];

  const tabs = [
    ...valueTabs,
    {
      label: 'Confirm',
      title: 'Confirm Appointment',
      icon: <Check />,
      content: (
        <div className="flex flex-col gap-3">
          {valueTabs.map((item, index) => (
            <div
              key={item.title}
              className="flex items-center gap-3 border-gray-200 pb-4 bg-gray-50 rounded-lg p-4"
            >
              <div className="text-gray-500 text-lg flex-shrink-0">
                {item.icon}
              </div>
              <div className="flex flex-col gap-1">
                <div className="font-medium text-gray-800 text-base">
                  {item.label}
                </div>
                <div className="text-gray-500 text-sm">
                  {item.renderFinalValue?.(formData[item.key] as any)}
                </div>
              </div>
              <div
                className="text-gray-500 text-sm cursor-pointer flex-shrink-0 ml-auto"
                onClick={() => {
                  setCurrentStep(index);
                }}
              >
                <Edit01 />
              </div>
            </div>
          ))}
          <Button
            block
            size="lg"
            onClick={async () => {
              const { appointmentSlot, location, provider } = formData;
              await createEhrAppointmentApi({
                department_id: location?.department_id ?? '',
                provider_id: provider?.id ?? '',
                appointment_type_id: appointmentSlot?.appointment_type_id ?? '',
                appointment_id: appointmentSlot?.appointment_id ?? '',
              });
              onComplete({
                content: JSON.stringify(formData),
                content_type: 'serialized_json',
                args,
              });
            }}
          >
            Confirm Appointment
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="flex flex-col w-full min-w-[320px] max-h-[592px] bg-white overflow-hidden rounded-lg shadow">
      <div className="flex flex-col items-center flex-shrink-0 p-3">
        <div className="text-sm text-gray-500 mb-2">
          Step {currentStep + 1} of {tabs.length}
        </div>
        <div className="flex w-full">
          {tabs.map((tab, index) => {
            const isCompleted = currentStep >= index;
            return (
              <div
                key={index}
                className={classNames(
                  'flex flex-col w-full items-center gap-2 border-b-4 py-2 px-1',
                  {
                    'border-primary': isCompleted,
                    'border-gray-200': !isCompleted,
                  }
                )}
              >
                {tab.icon && (
                  <div
                    className={classNames(
                      'h-9 w-9 flex items-center justify-center rounded-full',
                      {
                        'bg-primary text-white': currentStep >= index,
                        'bg-gray-100 text-gray-500': currentStep < index,
                      }
                    )}
                  >
                    {tab.icon}
                  </div>
                )}
                <div
                  className={classNames('font-medium text-sm text-nowrap', {
                    'text-primary': currentStep >= index,
                    'text-gray-500': currentStep < index,
                  })}
                >
                  {tab.label}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <div className="flex flex-col h-full overflow-hidden">
        <div className="flex items-center px-3 pb-2 gap-2 text-xl font-medium shadow">
          {currentStep > 0 && (
            <span
              className="cursor-pointer p-2 hover:bg-gray-100 rounded-lg"
              onClick={() => {
                setCurrentStep(currentStep - 1);
              }}
            >
              <ArrowLeft />
            </span>
          )}
          {tabs[currentStep].title}
        </div>
        <div className="flex flex-col h-full p-3 overflow-y-auto flex-1 min-h-[320px]">
          {tabs[currentStep].content}
        </div>
      </div>
    </div>
  );
};
